<template>
  <v-timeline-item small color="white" class="pb-3">
    <template v-slot:icon>
      <img
        v-if="icon"
        :src="icon"
        :style="iconStyle"
        class="icon"
        width="40"
        height="40"
      />
    </template>

    <v-card class="pa-3">
      <div :style="impactStyle" class="pa-0 mb-1 body pull-right">
        {{ severity }} Severity
      </div>

      <div class="pa-0 body mb-1">
        {{ categoryAndSubCategory }}
      </div>

      <h4
        class="card-title pa-0 mb-1 cursor-pointer"
        @click="showItemContent()"
      >
        {{ title }}
      </h4>

      <div v-if="showContent">
        <div class="pa-0 body my-1">{{ dateRange }}</div>
        <div class="pa-0 body" v-html="content"></div>
      </div>

      <div class="pa-0 body my-1">
        Impact Types:
        <span class="application-heading">{{ impactCategories }}</span>
      </div>

      <div v-if="countries" class="pa-0 body">
        <DetailsListItemLocations
          :countries="countries"
          theme="light"
          title="Countries"
        ></DetailsListItemLocations>
      </div>

      <div
        v-if="relatedCountries && relatedCountries.length > 0"
        class="pa-0 body mt-1"
      >
        <DetailsListItemLocations
          :countries="relatedCountries"
          theme="light"
          title="Related Countries"
        ></DetailsListItemLocations>
      </div>
      <v-icon
        @click="handleCopyClick"
        size="12"
        color="black"
        title="Copy event to Clipboard"
      >
        fas fa-clipboard
      </v-icon>
      <v-overlay :absolute="true" :value="screenShotting">
        <div class="copy-overlay-indicator">
          <v-icon size="50" color="white">fas fa-check</v-icon>
          <div><span class="application-heading">Copied</span></div>
        </div>
      </v-overlay>
    </v-card>
  </v-timeline-item>
</template>

<script>
import { dataToCopy } from "@/services/search/wod.service";
const EMITTED_ACTIONS = {
  SHOW_EVENT: "show-event",
};

export default {
  name: "CalendarEventsTimelineItem",
  components: {
    DetailsListItemLocations: () =>
      import("@/components/common/details/DetailsListItemLocations"),
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showContent: false,
      screenShotting: false,
      severityColors: {
        low: "green",
        medium: "orange",
        high: "red",
      },
    };
  },
  computed: {
    // possible categories for icons:
    // Holiday, Anniversary,
    // Political (Election), Political (Other),
    // Geopolitical, Security, Other
    icon() {
      let iconSrc = "default";
      if (this.category) {
        iconSrc = this.category
          .toLowerCase()
          .replace(" ", "_")
          .replace("(", "")
          .replace(")", "");
      }
      try {
        return require(`@/assets/svgs/calendarEvents/${iconSrc}.svg`);
      } catch (e) {
        return require(`@/assets/svgs/calendarEvents/default.svg`);
      }
    },
    iconStyle() {
      let color = "gray";
      if (this.severity) {
        color = this.severityColors[this.severity.toLowerCase()];
      }
      return {
        border: `2px solid ${color}`,
      };
    },
    impactStyle() {
      let color = "gray";
      if (this.severity) {
        color = this.severityColors[this.severity.toLowerCase()];
      }
      return {
        color: color,
      };
    },
    formatted() {
      return this.item?.properties?.formatted;
    },
    title() {
      return this.formatted?.title;
    },
    content() {
      return this.formatted?.content;
    },
    countries() {
      return this.formatted?.countries;
    },
    relatedCountries() {
      return this.formatted?.relatedCountries;
    },
    category() {
      return this.formatted?.category;
    },
    subCategory() {
      return this.formatted?.subCategory;
    },
    categoryAndSubCategory() {
      const arr = [];
      arr.push(this.category);
      if (this.subCategory.length) {
        arr.push(this.subCategory);
      }
      return arr.join(" | ");
    },
    dateType() {
      return this.formatted?.dateType;
    },
    impactCategories() {
      if (this.formatted?.impactCategories) {
        return this.formatted.impactCategories.join(", ");
      }
      return null;
    },
    severity() {
      return this.formatted?.severity;
    },
    dateRange() {
      return this.formatted?.dateRange;
    },
  },
  methods: {
    showItemContent() {
      this.$emit(EMITTED_ACTIONS.SHOW_EVENT, this.formatted);
      this.showContent = !this.showContent;
    },
    async handleCopyClick() {
      document.activeElement.blur();
      let item = [];

      const colsToCopy = [
        "categories",
        "title",
        "description",
        "impactTypes",
        "severity",
        "countries",
        "date",
      ];

      this.screenShotting = true;

      // adding fields to copy
      item.categories = this.categoryAndSubCategory;
      item.title = this.title;
      item.description = this.formatted.content.replace(/<[^>]*>/g, "");
      item.impacTypes = this.impactCategories;
      item.severity = this.severity;
      item.countries = this.countries;

      // if there is a releated country add them to copy
      if (this.formatted?.relatedCountries.length != 0) {
        item.relatedCountries = this.relatedCountries.map((rc) => rc.name);
        colsToCopy.push("relatedCountries");
      }

      item.date = this.dateRange;
      await dataToCopy(item, colsToCopy, true);

      setTimeout(() => {
        this.screenShotting = false;
      }, 500);
    },
  },
};
</script>

<style scoped>
.body {
  font-size: 0.8em;
  line-height: 1.4em;
}

.icon {
  border-radius: 20px;
}

.v-overlay {
  left: 0% !important;
  background-color: rgba(0, 0, 0, 0) !important;
}
.v-overlay--active.v-overlay {
  background-color: rgba(0, 0, 0, 0.307) !important;
  width: 100%;
}

.copy-overlay-indicator {
  width: 100px;
  height: 100px;
  background-color: green;
  border-radius: 10px;
  padding: 10px;
  text-align: center;
  font-size: large;
}
</style>
